import { Thumbnail } from '@/components/Thumbnail';
import { useToast } from '@/hooks/useToast';
import { pluralize } from '@/utils/format';
import { AddAPhotoOutlined, Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Dropzone } from '../intake/Dropzone';
import { ACCEPTED_PART_IMAGE_TYPES, addPartImageFiles, MAX_PART_IMAGES } from './partImageFiles';

const PICKED_THUMB_SIZE = 72;

const pastedImages = (event: ClipboardEvent): File[] =>
  [...(event.clipboardData?.files ?? [])].filter(file => file.type.startsWith('image/'));

const isTextField = (target: EventTarget | null): boolean =>
  target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;

const usePastedImages = (enabled: boolean, onPaste: (files: File[]) => void) => {
  useEffect(() => {
    if (!enabled) return;
    const handlePaste = (event: ClipboardEvent) => {
      const images = pastedImages(event);
      if (images.length === 0 || isTextField(event.target)) return;
      event.preventDefault();
      onPaste(images);
    };
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [enabled, onPaste]);
};

const PickedPhoto = ({ file, index, onRemove }: { file: File; index: number; onRemove: () => void }) => (
  <Box sx={{ position: 'relative', borderRadius: '8px', overflow: 'hidden' }} data-testid='part-image-picked'>
    <Thumbnail file={file} size={PICKED_THUMB_SIZE} noLink />
    <IconButton
      size='small'
      onClick={onRemove}
      aria-label={`Remove photo ${index + 1}`}
      sx={{ position: 'absolute', top: 2, right: 2, p: '2px', bgcolor: 'background.paper' }}
    >
      <Close fontSize='inherit' />
    </IconButton>
  </Box>
);

const MAX_CONTEXT_LENGTH = 1000;

export interface PartImageIntake {
  files: File[];
  autoSplit: boolean;
  context: string;
}

export interface PartImageIntakeDialogProperties {
  open: boolean;
  onClose: () => void;
  onContinue: (intake: PartImageIntake) => void;
}

export const PartImageIntakeDialog = ({ open, onClose, onContinue }: PartImageIntakeDialogProperties) => {
  const [files, setFiles] = useState<File[]>([]);
  const [autoSplit, setAutoSplit] = useState(false);
  const [context, setContext] = useState('');
  const { showToast } = useToast();

  const add = (added: File[]) => {
    const { files: next, problem } = addPartImageFiles(files, added);
    if (problem) showToast({ message: problem, type: 'warning' });
    setFiles(next);
  };
  usePastedImages(open, add);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (accepted, rejected) => add([...accepted, ...rejected.map(rejection => rejection.file)]),
    accept: ACCEPTED_PART_IMAGE_TYPES,
    multiple: true,
    useFsAccessApi: false,
  });

  const removeAt = (index: number) => setFiles(current => current.filter((_, position) => position !== index));
  const reset = () => {
    setFiles([]);
    setAutoSplit(false);
    setContext('');
  };
  const close = () => {
    reset();
    onClose();
  };
  const handleContinue = () => {
    reset();
    onContinue({ files, autoSplit, context });
  };

  return (
    <Dialog open={open} onClose={close} maxWidth='sm' fullWidth data-testid='part-image-intake'>
      <DialogTitle>Find from images</DialogTitle>
      <DialogContent>
        <Stack gap='16px'>
          <Dropzone {...getRootProps()} className={isDragActive ? 'active' : ''} data-testid='part-image-dropzone'>
            <input {...getInputProps()} data-testid='part-image-input' />
            <AddAPhotoOutlined color='action' fontSize='large' />
            <Typography variant='body2'>
              {isDragActive ? 'Drop the photos here' : 'Drop photos here, paste with Ctrl+V or ⌘V, or click to browse'}
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Up to {MAX_PART_IMAGES} JPEG, PNG, WebP, or HEIC photos
            </Typography>
          </Dropzone>
          {files.length > 0 && (
            <Stack direction='row' gap='8px' flexWrap='wrap'>
              {files.map((file, index) => (
                <PickedPhoto
                  key={`${file.name}-${file.lastModified}-${index}`}
                  file={file}
                  index={index}
                  onRemove={() => removeAt(index)}
                />
              ))}
            </Stack>
          )}
          <TextField
            label='Context (optional)'
            placeholder='e.g., off an M1A2 hydraulic line'
            value={context}
            onChange={event => setContext(event.target.value.slice(0, MAX_CONTEXT_LENGTH))}
            fullWidth
            size='small'
            helperText='Include any label, nameplate, or stamping in the photos.'
            slotProps={{ htmlInput: { 'data-testid': 'part-image-context' } }}
          />
          <Stack gap='2px'>
            <FormControlLabel
              control={<Checkbox checked={autoSplit} onChange={event => setAutoSplit(event.target.checked)} />}
              label='Auto-split multiple parts'
            />
            <Typography variant='caption' color='text.secondary'>
              Check this when the photos show different parts you want searched one by one; the whole photos are
              searched, so there is no crop step. Leave it off to search everything shown as one part, like a whole kit.
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={close}>Cancel</Button>
        <Button
          variant='contained'
          disabled={files.length === 0}
          onClick={handleContinue}
          data-testid='part-image-intake-continue'
        >
          {files.length === 0 ? 'Continue' : `Continue with ${pluralize(files.length, 'photo', 'photos')}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
